import { ChannelType } from '@vision/ui/enums';
import type { ApiResponse } from '@vision/ui/interfaces';
import type {
  AssignChannelFlowArgs,
  AssignChannelFlowRequestDto,
  ChannelCampaignListResponseDto,
  ChannelDashboardResponseDto,
  ChannelFlowSearchItemDto,
  ChannelListResponseDto,
  GetChannelFlowPagesArgs,
  GetChannelsArgs,
  GetChannelsByTypeArgs,
  GetChannelsRequestDto,
  SearchChannelFlowsRequestDto,
  SearchChannelFlowsResponseDto,
  SearchChannelsArgs,
  SearchChannelsRequestDto,
  SearchChannelsResponseDto,
} from './channel-lists.api.types';
import type {
  ChannelCampaignListResult,
  ChannelDashboard,
  ChannelFlowSearchItem,
  ChannelFlowSearchPage,
  ChannelListResult,
  ChannelListType,
  ChannelSearchPage,
  ChannelTypeSummary,
} from './types';

// Reverses Rails `underscore`: `google_play` → `GooglePlay`.
function toNodeType(channelTypeKey: string): string {
  return channelTypeKey
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

export function toApiSearchChannels({ query, page }: SearchChannelsArgs): SearchChannelsRequestDto {
  return { q: query, ...(page ? { page } : {}) };
}

export function fromApiSearchChannels({ data, meta }: SearchChannelsResponseDto): ChannelSearchPage {
  return {
    items: data.map(({ id, name, type }) => ({ id, name, type })),
    nextPage: meta.pagination.next,
  };
}

export function toApiGetChannelFlowPage(
  { searchText }: GetChannelFlowPagesArgs,
  page: number,
): SearchChannelFlowsRequestDto {
  return { page, ...(searchText ? { q: searchText } : {}) };
}

export function fromApiChannelFlowSearchItem({
  id,
  is_conversational,
  name,
  node_count,
}: ChannelFlowSearchItemDto): ChannelFlowSearchItem {
  return { channelCount: node_count, id, isConversational: is_conversational, name };
}

// VLazyLoadSelect requires the filter list response shape; flow search has no filter metadata.
export function fromApiChannelFlowSearch({
  data,
  meta,
}: SearchChannelFlowsResponseDto): ApiResponse<ChannelFlowSearchItem[]> {
  return {
    data: data.map(fromApiChannelFlowSearchItem),
    meta: {
      filter_selections: [],
      filters: [],
      generic_search_keys: [],
      pagination: meta.pagination,
      searchable: true,
    },
    object: 'collection',
  };
}

export function fromApiChannelFlowPage({ data, meta }: SearchChannelFlowsResponseDto): ChannelFlowSearchPage {
  return { items: data.map(fromApiChannelFlowSearchItem), nextPage: meta.pagination.next ?? null };
}

export function fromApiChannelList({ data, meta }: ChannelListResponseDto): ChannelListResult {
  return {
    items: data.map((channel) => ({
      createdAt: new Date(channel.created_at),
      flow: channel.flow,
      id: channel.id,
      logoUrl: channel.logo_url,
      name: channel.name,
      status: channel.status,
      themeEngine: channel.theme_engine,
      type: channel.type,
    })),
    meta,
  };
}

export function toApiGetChannels({ category }: GetChannelsArgs): GetChannelsRequestDto {
  return category ? { category } : {};
}

// Each type list's path, e.g. `channels/link`; the save and detail paths start from it.
export const channelListTypePaths = {
  [ChannelType.APP_STORE]: 'app_store',
  [ChannelType.CATI]: 'cati',
  [ChannelType.EMAIL]: 'email',
  [ChannelType.GLASSDOOR]: 'glassdoor',
  [ChannelType.GOOGLE_MAP]: 'google_map',
  [ChannelType.GOOGLE_PLAY]: 'google_play',
  [ChannelType.IVR]: 'ivr',
  [ChannelType.LINK]: 'link',
  [ChannelType.MOBILE]: 'mobile',
  [ChannelType.PUSH_NOTIFICATION]: 'push',
  [ChannelType.SIKAYET_VAR]: 'sikayet_var',
  [ChannelType.SMS]: 'sms',
  [ChannelType.TRUSTPILOT]: 'trustpilot',
  [ChannelType.WEB_WIDGET]: 'web_widget',
  [ChannelType.WHATSAPP]: 'whatsapp',
} satisfies Record<ChannelListType, string>;

export function toApiChannelsByTypeUrl({ accountId, channelType }: GetChannelsByTypeArgs): string {
  return `/v2/accounts/${accountId}/channels/${channelListTypePaths[channelType]}`;
}

const messageCampaignTypes = new Set<ChannelListType>([
  ChannelType.PUSH_NOTIFICATION,
  ChannelType.SMS,
  ChannelType.WHATSAPP,
]);

// Each item is a campaign, widget or SDK; the list shows its channel, whose type the endpoint implies.
export function fromApiChannelsByType(
  { data, meta }: ChannelCampaignListResponseDto,
  channelType: ChannelListType,
): ChannelCampaignListResult {
  return {
    items: data.map((campaign) => ({
      campaignId: campaign.id,
      createdAt: new Date(campaign.created_at),
      flow: campaign.flow && {
        id: campaign.flow.id,
        isConversational: campaign.flow.is_conversational ?? false,
        name: campaign.flow.name,
      },
      emailCampaign:
        channelType === ChannelType.EMAIL
          ? { languageName: campaign.language?.name ?? null, subject: campaign.subject ?? null }
          : null,
      id: campaign.node.id,
      ivrFeedbackWebBaseUrl: channelType === ChannelType.IVR ? (campaign.node.feedback_web_base_url ?? null) : null,
      linkChannelSettings:
        channelType === ChannelType.LINK
          ? {
              multipleFeedbackDelayPeriod: campaign.multiple_feedback_delay_period ?? null,
              preventMultipleFeedback: campaign.prevent_multiple_feedback ?? false,
            }
          : null,
      logoUrl: campaign.node.logo_url,
      messageCampaign: messageCampaignTypes.has(channelType)
        ? { languageName: campaign.language?.name ?? null, message: campaign.message ?? null }
        : null,
      mobileSdk:
        channelType === ChannelType.MOBILE
          ? { code: campaign.code ?? null, feedbackWebBaseUrl: campaign.node.feedback_web_base_url ?? null }
          : null,
      name: campaign.node.name,
      status: campaign.node.status,
      surveyLink:
        channelType === ChannelType.LINK
          ? {
              feedbackWebBaseUrl: campaign.node.feedback_web_base_url ?? null,
              stableUrl: campaign.node.survey_link?.url ?? null,
              type: campaign.node.link_type ?? null,
            }
          : null,
      themeEngine: campaign.node.theme_engine,
      type: channelType,
      widgetCode: channelType === ChannelType.WEB_WIDGET ? (campaign.code ?? null) : null,
    })),
    meta,
  };
}

export function toApiAssignChannelFlow({ flowId }: AssignChannelFlowArgs): AssignChannelFlowRequestDto {
  return { flow_id: flowId };
}

export function fromApiChannelDashboard({ data }: ChannelDashboardResponseDto): ChannelDashboard {
  const { account, ...summaries } = data;
  const channelTypes: Record<string, ChannelTypeSummary> = {};

  for (const [key, summary] of Object.entries(summaries)) {
    if (!('count' in summary)) {
      continue;
    }

    channelTypes[toNodeType(key)] = {
      accessDeniedReason: summary.access_denied_reason ?? null,
      canAccess: summary.can_access,
      count: summary.count,
      providerMissingReason: summary.provider_missing_reason ?? null,
    };
  }

  return {
    account: { logoUrl: account.logo_url, name: account.name },
    channelTypes,
  };
}
